import { initCheckoutBedbankSession } from 'actions/CheckoutActions'
import { getBedbankSessionGroupingKey } from 'lib/checkout/bedbankSessionGroupingKey'
import { isBedbankItem } from 'lib/checkout/checkoutUtils'
import { groupBy } from 'lib/array/arrayUtils'
import { mapBedbankBoardCode } from 'lib/hotels/bedbankUtils'
import type { AppDispatch } from 'src/client/store'

function getBedbankBoardCodeFromRoomRateId(roomRateId: string): App.RateBoardCode | undefined {
  const boardCode = roomRateId.split('|')[2]
  const mappedBoardCode = mapBedbankBoardCode(boardCode)
  return mappedBoardCode === 'LE' ? undefined : mappedBoardCode
}

function getNormalisedBedbankBoardCode(boardCode: string | undefined): App.RateBoardCode | undefined {
  if (!boardCode) return undefined
  const mappedBoardCode = mapBedbankBoardCode(boardCode)
  return mappedBoardCode === 'LE' && boardCode !== 'LE' ? undefined : mappedBoardCode
}

function getBedbankRefundableFromRoomRateId(roomRateId: string): boolean | undefined {
  const refundabilityCode = roomRateId.split('|')[4]
  switch (refundabilityCode) {
    case 'NP':
      return false
    case 'RF':
      return true
    default:
      return undefined
  }
}

function prepareTripChatBedbankItemForSessionRefresh(item: App.Checkout.BedbankHotelItem): App.Checkout.BedbankHotelItem {
  return {
    ...item,
    boardCode: getNormalisedBedbankBoardCode(item.boardCode) ?? getBedbankBoardCodeFromRoomRateId(item.roomRateId),
    refundable: item.refundable ?? getBedbankRefundableFromRoomRateId(item.roomRateId),
    isFlightBundle: item.isFlightBundle ?? false,
  }
}

export async function refreshTripChatBedbankItemSessions(
  items: Array<App.Checkout.AnyItem>,
  dispatch: AppDispatch,
): Promise<Array<App.Checkout.AnyItem>> {
  const state = dispatch((_dispatch: AppDispatch, getState: () => App.State) => getState())
  const currentRegionCode = state.geo.currentRegionCode
  const isSpoofed = state.auth.account.isSpoofed

  const bedbankItems = items
    .filter(isBedbankItem)
    .map(prepareTripChatBedbankItemForSessionRefresh)
  const refreshedBedbankItems = new Map<string, App.Checkout.BedbankHotelItem>()

  const groupedBedbankItems = Array.from(groupBy(bedbankItems, getBedbankSessionGroupingKey).values())

  for (const bedbankItemGroup of groupedBedbankItems) {
    const results = await initCheckoutBedbankSession(dispatch, state, currentRegionCode, bedbankItemGroup, isSpoofed)
    for (let i = 0; i < bedbankItemGroup.length; i++) {
      const item = bedbankItemGroup[i]
      const result = results[i]
      const rateIdentityChanged = item.roomRateId !== result.roomRateId ||
        item.rateGroupId !== result.rateGroupId

      refreshedBedbankItems.set(item.itemId, {
        ...item,
        sessionId: result.sessionId,
        roomRateId: result.roomRateId,
        rateGroupId: result.rateGroupId,
        bedGroupId: !item.bedGroupId || rateIdentityChanged ? result.defaultBedGroupId : item.bedGroupId,
      })
    }
  }

  return items.map(item => refreshedBedbankItems.get(item.itemId) ?? item)
}
